import { useCallback, useMemo, type ReactNode } from "react";
import type { z } from "zod";
import getMetricSpec, { getFormatter } from "../../../merchandising/utils/metrics";
import type { campaignStatsHourlyItemSchema } from "../../../../zodios/apiDefinitions/api/campaignStatsHourly";
import type { CellKey } from "../types";
import { createCellKey } from "../utils";

type CampaignStatsHourlyItem = z.infer<typeof campaignStatsHourlyItemSchema>;

interface UseDaypartingValuesProps {
  data: CampaignStatsHourlyItem[];
  metricId: string;
  marketPlace: { marketPlace: string };
}

interface CellValue {
  value: number | null;
  formattedValue: ReactNode;
}

export function useDaypartingValues(props: UseDaypartingValuesProps): (hour: number, day: number) => CellValue {
  const { data, metricId, marketPlace } = props;

  const valueMap = useMemo(() => {
    const metricSpec = getMetricSpec(metricId);
    const formatter = getFormatter(marketPlace, metricSpec.format, metricSpec);

    if (data.length === 0) {
      return new Map<CellKey, CellValue>();
    }

    const map = new Map<CellKey, CellValue>();
    for (const record of data) {
      const value = metricSpec.accessor(record);
      if (record.hour != null && record.weekday != null) {
        const key = createCellKey(record.hour, record.weekday - 1);
        map.set(key, {
          value,
          formattedValue: value !== null ? formatter(value) : "",
        });
      }
    }

    return map;
  }, [data, metricId, marketPlace]);

  return useCallback(
    (hour: number, day: number): CellValue => {
      const key = createCellKey(hour, day);
      return valueMap.get(key) ?? { value: null, formattedValue: "" };
    },
    [valueMap],
  );
}
